import React, { useEffect, useState, lazy, memo } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ClockIcon, HeartIcon, PiggyBankIcon, ShieldCheckIcon, SmileIcon, BookOpenIcon, CalendarIcon, ListChecksIcon, AlertCircleIcon, MoveUpIcon, VolumeIcon, LightbulbIcon } from 'lucide-react';
export function ProductSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const images = [{
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/tixqS33CbZvk2mrbX8UqzY/BabyFoodMadeSimple.jpg',
    alt: 'Baby Food Made Simple recipe book with colorful purees'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/cUXzg1DtBGrKVrcj1P2gRo/BabyFoodMadeSimple3.jpg',
    alt: 'Mother preparing baby food with the recipe book'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/vi76xjzU4PxnVuUb2z8wYG/Baby-Food_Made_Simple.jpg',
    alt: 'Baby Food Made Simple book cover showing puree recipes'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/vdaYQUyvVJF5DLbvi2T3Zj/BabyFoodMadeSimple2.jpg',
    alt: 'Mother reading Baby Food Made Simple book with prepared purees'
  }];
  // Preload images after initial render
  useEffect(() => {
    // Defer image preloading to not block initial page load
    const timeoutId = setTimeout(() => {
      const imagePromises = images.map(image => {
        return new Promise(resolve => {
          const img = new Image();
          img.src = image.src;
          img.onload = resolve;
          img.onerror = resolve;
        });
      });
      Promise.all(imagePromises).then(() => {
        setImagesLoaded(true);
      });
    }, 1500); // Delay preloading by 1.5 seconds
    return () => clearTimeout(timeoutId);
  }, []);
  const nextSlide = () => {
    setCurrentSlide(prev => prev === images.length - 1 ? 0 : prev + 1);
  };
  const prevSlide = () => {
    setCurrentSlide(prev => prev === 0 ? images.length - 1 : prev - 1);
  };
  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };
  return <section className="py-8 bg-white" data-id="element-337">
      <div className="max-w-6xl mx-auto px-2 sm:px-6 lg:px-8" data-id="element-338">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-339">
          <h2 className="text-3xl font-bold text-blue-900 mb-4" data-id="element-340">
            The Solution: "Baby Food Made Simple"
          </h2>
          <div className="h-1 w-20 bg-yellow-500 mx-auto mb-6" data-id="element-341"></div>
          <p className="text-lg text-gray-700" data-id="element-342">
            A digital recipe book with soul, science, and clinical structure so
            your baby can:
          </p>
          <ul className="text-lg text-gray-700 mt-4 space-y-2" data-id="element-343">
            <li data-id="element-344">Eat joyfully from the very first bite</li>
            <li data-id="element-345">Progress through each stage without fear</li>
            <li data-id="element-346">And you feel calm, confident, and in control</li>
          </ul>
          <p className="text-lg font-bold text-blue-900 mt-4" data-id="element-347">
            No guesswork. Just follow the plan.
          </p>
        </div>
        <div className="mt-12 md:flex md:items-center md:gap-8" data-id="element-348">
          <div className="md:w-1/2 mb-8 md:mb-0" data-id="element-349">
            {/* Carousel container */}
            <div className="relative rounded-xl overflow-hidden shadow-lg bg-gray-100 aspect-[3/4] max-w-md mx-auto" data-id="element-350">
              {/* Images */}
              {images.map((image, index) => <div key={index} className={`absolute inset-0 transition-opacity duration-500 ${currentSlide === index ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-id="element-351">
                  <img src={image.src} alt={image.alt} className="w-full h-full object-cover" loading="lazy" data-id="element-352" />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 text-white" data-id="element-353">
                    <span className="text-sm font-medium" data-id="element-354">
                      Digital Recipe Book
                    </span>
                  </div>
                </div>)}
              {/* Navigation arrows */}
              <button onClick={prevSlide} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full shadow-md hover:bg-white transition-colors" aria-label="Previous slide" data-id="element-355">
                <ChevronLeftIcon className="h-5 w-5 text-gray-800" data-id="element-356" />
              </button>
              <button onClick={nextSlide} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full shadow-md hover:bg-white transition-colors" aria-label="Next slide" data-id="element-357">
                <ChevronRightIcon className="h-5 w-5 text-gray-800" data-id="element-358" />
              </button>
              {/* Indicators */}
              <div className="absolute bottom-4 right-4 flex space-x-2" data-id="element-359">
                {images.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`h-2 w-2 rounded-full ${currentSlide === index ? 'bg-gray-600' : 'bg-gray-300'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-360" />)}
              </div>
            </div>
          </div>
          <div className="md:w-1/2" data-id="element-361">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-362">
              What's Inside the System?
            </h3>
            <div className="space-y-6" data-id="element-363">
              <div className="flex items-start space-x-4" data-id="element-364">
                <div className="bg-blue-100 p-2 rounded-full" data-id="element-365">
                  <BookOpenIcon className="h-6 w-6 text-blue-600" data-id="element-366" />
                </div>
                <div data-id="element-367">
                  <h4 className="text-lg font-bold text-gray-900" data-id="element-368">
                    Recipes organized by age (6–7, 8–9, 10–12 months)
                  </h4>
                </div>
              </div>
              <div className="flex items-start space-x-4" data-id="element-369">
                <div className="bg-blue-100 p-2 rounded-full" data-id="element-370">
                  <VolumeIcon className="h-6 w-6 text-blue-600" data-id="element-371" />
                </div>
                <div data-id="element-372">
                  <h4 className="text-lg font-bold text-gray-900" data-id="element-373">
                    Scientifically backed texture progression
                  </h4>
                </div>
              </div>
              <div className="flex items-start space-x-4" data-id="element-374">
                <div className="bg-blue-100 p-2 rounded-full" data-id="element-375">
                  <ShieldCheckIcon className="h-6 w-6 text-blue-600" data-id="element-376" />
                </div>
                <div data-id="element-377">
                  <h4 className="text-lg font-bold text-gray-900" data-id="element-378">
                    Safety tips and anti-choking protocol
                  </h4>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-16" data-id="element-379">
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-10" data-id="element-380">
            It's not a recipe book. It's an emotional roadmap.
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-381">
            <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow" data-id="element-382">
              <div className="flex items-center mb-4" data-id="element-383">
                <CalendarIcon className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0" data-id="element-384" />
                <h4 className="text-lg font-bold text-gray-900" data-id="element-385">
                  Weekly meal plans with practical examples
                </h4>
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow" data-id="element-386">
              <div className="flex items-center mb-4" data-id="element-387">
                <ListChecksIcon className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0" data-id="element-388" />
                <h4 className="text-lg font-bold text-gray-900" data-id="element-389">
                  List of forbidden foods backed by current science
                </h4>
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow" data-id="element-390">
              <div className="flex items-center mb-4" data-id="element-391">
                <MoveUpIcon className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0" data-id="element-392" />
                <h4 className="text-lg font-bold text-gray-900" data-id="element-393">
                  Step-by-step allergen introduction
                </h4>
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow" data-id="element-394">
              <div className="flex items-center mb-4" data-id="element-395">
                <LightbulbIcon className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0" data-id="element-396" />
                <h4 className="text-lg font-bold text-gray-900" data-id="element-397">
                  Hunger and fullness cues few moms know
                </h4>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-16 text-center max-w-3xl mx-auto" data-id="element-398">
          <p className="text-lg font-medium text-gray-700 mb-8" data-id="element-399">
            A silent transformation, plate by plate... from chaos… to calm.
          </p>
          <div className="bg-gray-50 p-4 rounded-xl" data-id="element-400">
            <h3 className="text-2xl font-bold text-blue-900 mb-4" data-id="element-401">
              What Will You Gain from This System?
            </h3>
            <ul className="text-gray-700 space-y-3 text-left pl-6 mt-6" data-id="element-402">
              <li className="flex items-start" data-id="element-403">
                <span className="font-bold mr-2" data-id="element-404">More Time:</span> Recipes ready
                in 10–15 minutes
              </li>
              <li className="flex items-start" data-id="element-405">
                <span className="font-bold mr-2" data-id="element-406">More Peace:</span> Your baby
                eats without tears
              </li>
              <li className="flex items-start" data-id="element-407">
                <span className="font-bold mr-2" data-id="element-408">More Savings:</span> Simple,
                budget-friendly ingredients
              </li>
              <li className="flex items-start" data-id="element-409">
                <span className="font-bold mr-2" data-id="element-410">More Confidence:</span>{' '}
                Balanced nutrition, backed by my protocol
              </li>
              <li className="flex items-start" data-id="element-411">
                <span className="font-bold mr-2" data-id="element-412">More Connection:</span> Every
                meal becomes a joyful memory
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>;
}